/**
 * Resolve a campaign-detail `:id` to a `Campaign`, from the mock pool first and the
 * Casino Ad Service second.
 *
 * The acquisition campaigns browser now lists real ad-service campaigns, so a
 * detail link can carry an id that is not in the mock pool. This looks it up in the
 * mock pool (the still-mock suggested/ended samples) and, if absent, fetches the
 * real campaign by id so the detail opens (real title / artwork / status) instead
 * of rendering nothing. Deep-link safe: the id lives in the URL, so a direct visit
 * resolves the same way as a click.
 *
 * The detail PAYLOAD (performance, top sites, creatives beyond the thumbnail) is
 * still the shared generic mock with this identity stamped on it (see the dispatcher
 * + `utils/card-identity`); only the lookup is wired here.
 */
import React from 'react';

import type { Campaign } from '../../mock-data/types';
import { acquisitionData } from '../../mock-data/acquisition';
import { type AdCampaignDetail, getAdCampaign } from '../acquisition/api/casino-ad-service';
import { toCampaign } from '../acquisition/campaign-mapping';

/**
 * The resolved campaign plus, for an API (non-mock) campaign, the RAW ad-service
 * `detail` it was fetched from — the page reads `start_date` (the hero's lifetime Ad
 * Spend window), `network` (the asset channel tag) and the generated `assets` off it.
 * `detail` is undefined for a mock-pool campaign (which keeps its sample payload).
 */
export type ResolvedCampaign = { campaign: Campaign | undefined; detail: AdCampaignDetail | undefined; loading: boolean };

export const useResolvedCampaign = (id: string | undefined, tenantId: string | undefined): ResolvedCampaign => {
  // The mock pool is a stable module constant, so this find is referentially stable
  // per id (safe as an effect dep, no refetch loop).
  const mock = id ? acquisitionData.campaigns.pool.find((campaign) => campaign.id === id) : undefined;
  const [fetched, setFetched] = React.useState<Campaign | undefined>(undefined);
  const [detail, setDetail] = React.useState<AdCampaignDetail | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    // Nothing to fetch: no id, or the mock pool already has it.
    if (!id || mock) {
      setFetched(undefined);
      setDetail(undefined);
      setLoading(false);
      return;
    }
    // A real (non-mock) id needs the active brand's tenant to reach the Ad Service
    // proxy. Until a brand resolves, stay loading rather than flashing "unknown id".
    if (!tenantId) {
      setFetched(undefined);
      setDetail(undefined);
      setLoading(true);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    setFetched(undefined);
    setDetail(undefined);
    getAdCampaign(id, tenantId, signal)
      .then((apiDetail) => {
        if (!signal.aborted) {
          // The detail extends the summary, so it carries both the card fields and
          // the assets (for the thumbnail). Keep the raw detail for the wired figures.
          // A campaign the studio never shows (Failed / Deleted / an unrecognised
          // state) maps to null: treat that deep link exactly like an unknown id
          // rather than opening a detail page for a campaign listed nowhere.
          const campaign = toCampaign(apiDetail, apiDetail);
          setFetched(campaign ?? undefined);
          setDetail(campaign ? apiDetail : undefined);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!signal.aborted) {
          setFetched(undefined);
          setDetail(undefined);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [id, mock, tenantId]);

  return { campaign: mock ?? fetched, detail, loading };
};

export default useResolvedCampaign;
